import { Plus, X } from 'lucide-react';

interface OperatingHoursProps {
  formik: any;
  fieldPath?: string;
  types: Array<{
    key: string;
    label: string;
  }>;
  className?: string;
}

interface TimeSlot {
  openTime: string;
  closeTime: string;
}

interface DayHours {
  isOpen: boolean;
  time: TimeSlot[];
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface OperatingHoursData {
  [key: string]: {
    hours: {
      [key: number]: DayHours;
    };
  };
}

export function OperatingHours({
  formik,
  fieldPath = 'operation',
  types,
  className = ''
}: OperatingHoursProps) {

  const days = [0, 1, 2, 3, 4, 5, 6];
  const daysName = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const getFieldValue = (path: string) => {
    return path.split('.').reduce((obj, key) => obj?.[key], formik.values);
  };

  const setFieldValue = (path: string, value: any) => {
    formik.setFieldValue(path, value);
  };

  const getTimeSlots = (day: number, type: string): TimeSlot[] => {
    const data = getFieldValue(`${fieldPath}.${type}.hours.${day}`);
    return data?.time || [];
  };

  const addTimeSlot = (day: number, type: string) => {
    const currentData = getFieldValue(`${fieldPath}.${type}.hours.${day}`) || { isOpen: false, time: [] };
    const currentSlots = currentData.time || [];
    const newSlots = [...currentSlots, { openTime: '', closeTime: '' }];

    const updatedData = {
      isOpen: true,
      time: newSlots
    };

    setFieldValue(`${fieldPath}.${type}.hours.${day}`, updatedData);
  };

  const removeTimeSlot = (day: number, type: string, index: number) => {
    const currentData = getFieldValue(`${fieldPath}.${type}.hours.${day}`) || { isOpen: false, time: [] };
    const currentSlots = currentData.time || [];

    const newSlots = [...currentSlots];
    newSlots.splice(index, 1);

    const updatedData = {
      isOpen: newSlots.length === 0 ? false : true,
      time: newSlots
    };

    setFieldValue(`${fieldPath}.${type}.hours.${day}`, updatedData);
  };

  const updateTimeSlot = (day: number, type: string, index: number, field: 'openTime' | 'closeTime', value: string) => {
    const currentSlots = getTimeSlots(day, type);
    const updatedSlots = currentSlots.map((slot, i) =>
      i === index ? { ...slot, [field]: value } : slot
    );

    const currentData = getFieldValue(`${fieldPath}.${type}.hours.${day}`) || { isOpen: false, time: [] };
    const updatedData = { ...currentData, time: updatedSlots };

    setFieldValue(`${fieldPath}.${type}.hours.${day}`, updatedData);
  };

  const updateIsOpen = (day: number, type: string, isOpen: boolean) => {
    const currentData = getFieldValue(`${fieldPath}.${type}.hours.${day}`) || { isOpen: false, time: [] };
    const updatedData = { ...currentData, isOpen };

    if (!isOpen) {
      updatedData.time = [];
    }

    setFieldValue(`${fieldPath}.${type}.hours.${day}`, updatedData);
  };

  const renderDaySection = (day: number, type: string) => {
    const isOpen = getFieldValue(`${fieldPath}.${type}.hours.${day}.isOpen`) || false;
    const timeSlots = getTimeSlots(day, type);

    return (
      <div key={day} className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12 mt-4">
        <div className="xl:col-span-12">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <label
                htmlFor={`${fieldPath}.${type}.hours.${day}.isOpen`}
                className="flex flex-col gap-4 p-5 border rounded-md cursor-pointer md:flex-row border-slate-200 dark:border-zink-500 peer-checked:border-purple-500 dark:peer-checked:border-purple-700">
                <div className="flex flex-col items-center gap-3">
                  <span className="flex items-center gap-3">
                    <input
                      id={`${fieldPath}.${type}.hours.${day}.isOpen`}
                      className="w-4 h-4 border appearance-none cursor-pointer bg-slate-100 border-slate-200 dark:bg-zink-600 dark:border-zink-500 checked:bg-purple-500 checked:border-purple-500 dark:checked:bg-purple-500 dark:checked:border-purple-500 checked:disabled:bg-purple-400 checked:disabled:border-purple-400 peer"
                      type="checkbox"
                      checked={isOpen}
                      onChange={(e) => updateIsOpen(day, type, e.target.checked)}
                    />
                    <span className="block font-semibold text-15">{daysName[day]}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => addTimeSlot(day, type)}
                    className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-700 dark:hover:text-blue-400"
                  >
                    <Plus size={16} />
                    Add Time Slot
                  </button>
                </div>
                {isOpen && (
                  <div className="ml-8 space-y-3">
                    {timeSlots.map((slot, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="time"
                          placeholder="Open Time"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          value={slot.openTime || ""}
                          onChange={(e) => updateTimeSlot(day, type, index, 'openTime', e.target.value)}
                        />
                        <input
                          type="time"
                          placeholder="Close Time"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          value={slot.closeTime || ""}
                          onChange={(e) => updateTimeSlot(day, type, index, 'closeTime', e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => removeTimeSlot(day, type, index)}
                          className="p-1 text-red-500 hover:text-red-700 dark:hover:text-red-400"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getGridCols = () => {
    const count = types.length;
    if (count === 1) return 'xl:col-span-12';
    if (count === 2) return 'xl:col-span-6';
    if (count === 3) return 'xl:col-span-4';
    if (count === 4) return 'xl:col-span-3';
    return 'xl:col-span-6';
  };

  return (
    <div className={`mt-4 ${className}`}>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12 mt-4">
        {types.map((type) => (
          <div key={type.key} className={getGridCols()}>
            <label htmlFor={`${fieldPath}.${type.key}`} className="inline-block mb-2 text-base font-medium">
              {type.label}
            </label>
            {days.map((day) => renderDaySection(day, type.key))}
          </div>
        ))}
      </div>
    </div>
  );
}